import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axiosInstance from "../../utils/axiosInstance";

/**
 * M2: the active squad an action leaves behind when it settles discovery, or
 * `undefined` for any other action. Read by action type so this slice does not
 * import the user slice (several suites mock it). The same function sits in
 * store/tasks/slice.js and store/planTasks/slice.js; the squad itself is
 * decided once, by the user slice, and carried as `settledSquadId`.
 */
const settledActiveSquadOf = (action) => {
  // A superseded discovery reply settles nothing: the user slice ignores it.
  if (action.payload?.superseded === true) return undefined;
  switch (action.type) {
    case "user/fetchExperienceContexts/fulfilled":
      return action.payload?.settledSquadId ?? null;
    case "user/fetchExperienceContexts/rejected":
      return action.payload?.status === 409 ? null : undefined;
    case "user/resetExperienceContext":
      return null;
    default:
      return undefined;
  }
};

/* The marker a request id takes when a context change voids it. */
const CONTEXT_CHANGED = "context_changed";

// Generate a simple unique ID
const generateId = () =>
  `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * Type-robust session-task id equality.
 *
 * sessionTasks ids originate as MySQL bigint primary keys - always
 * positive integers - and arrive as JS numbers on the REST path
 * (fetchPlanSession, createSessionTask, claim/unclaim/promote thunks).
 * But the SAME ids also arrive over the WebSocket relay: PlanningSessionService
 * broadcasts `taskId`/`task.id` as PHP ints, which are then re-serialized
 * by an external socket relay (NotificationService::sendSocketMessage POSTs
 * to {relay}/special, which forwards the payload to clients) - a boundary
 * that commonly stringifies numeric fields. A strict `===` match between a
 * number-typed local id and a string-typed broadcast id silently FAILS, so
 * a claim/edit/finalize never reflects locally (WebSocketContext's
 * taskExists() then fires a full refetch, and during that latency the UI
 * shows the stale state until a manual refresh). Compare via Number on both
 * sides so the match is robust regardless of which transport delivered the
 * id. Confirmed primary root cause of "claim sometimes needs a refresh"
 * (converged platform-engineer + codex-engineer diagnosis, 2026-06-15).
 *
 * Scope: ONLY for session-task ids (server-backed). The legacy
 * savedTasks/planningTasks reducers use client-generated string ids
 * (generateId) compared to client-passed string ids - no cross-transport
 * drift - and are intentionally left on strict === .
 */
export const sameSessionTaskId = (a, b) => Number(a) === Number(b);

/**
 * Fetch or create a planning session for a squad + capsule.
 * Calls GET /api/v1/plan-session/{squadId}/{capsuleId}
 * Returns the session snapshot with tasks and presence.
 */
export const fetchPlanSession = createAsyncThunk(
  "planTasks/fetchPlanSession",
  async ({ squadId, capsuleId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance(
        `/plan-session/${squadId}/${capsuleId}`,
        "get",
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("[planTasks] Failed to fetch plan session:", error);
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load planning session",
      );
    }
  },
);

/**
 * Create a task within a planning session.
 * POST /api/v1/plan-session/{sessionId}/task
 */
export const createSessionTask = createAsyncThunk(
  "planTasks/createSessionTask",
  async ({ sessionId, title }, { rejectWithValue, getState, dispatch }) => {
    try {
      const response = await axiosInstance(
        `/plan-session/${sessionId}/task`,
        "post",
        { title },
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("[planTasks] Failed to create task:", error);
      const session = getState().planTasks.session;
      if (session?.squad_id && session?.capsule_id) {
        dispatch(
          fetchPlanSession({
            squadId: session.squad_id,
            capsuleId: session.capsule_id,
          }),
        );
      }
      return rejectWithValue(
        error?.response?.data?.message || "Failed to create task",
      );
    }
  },
);

/**
 * Update a task title within a planning session.
 * PATCH /api/v1/plan-session/{sessionId}/task/{taskId}
 */
export const updateSessionTaskTitle = createAsyncThunk(
  "planTasks/updateSessionTaskTitle",
  async (
    { sessionId, taskId, title },
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const response = await axiosInstance(
        `/plan-session/${sessionId}/task/${taskId}`,
        "patch",
        { title },
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("[planTasks] Failed to update task:", error);
      const session = getState().planTasks.session;
      if (session?.squad_id && session?.capsule_id) {
        dispatch(
          fetchPlanSession({
            squadId: session.squad_id,
            capsuleId: session.capsule_id,
          }),
        );
      }
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update task",
      );
    }
  },
);

/**
 * Delete a task from a planning session.
 * DELETE /api/v1/plan-session/{sessionId}/task/{taskId}
 */
export const deleteSessionTask = createAsyncThunk(
  "planTasks/deleteSessionTask",
  async ({ sessionId, taskId }, { rejectWithValue, getState, dispatch }) => {
    try {
      await axiosInstance(
        `/plan-session/${sessionId}/task/${taskId}`,
        "delete",
      );
      return taskId;
    } catch (error) {
      console.error("[planTasks] Failed to delete task:", error);
      const session = getState().planTasks.session;
      if (session?.squad_id && session?.capsule_id) {
        dispatch(
          fetchPlanSession({
            squadId: session.squad_id,
            capsuleId: session.capsule_id,
          }),
        );
      }
      return rejectWithValue(
        error?.response?.data?.message || "Failed to delete task",
      );
    }
  },
);

/**
 * Claim a task within a planning session (Phase 2).
 * PATCH /api/v1/plan-session/{sessionId}/task/{taskId}
 * Backend enforces: assigned_to_user_id must equal authenticated user.
 */
export const claimSessionTaskApi = createAsyncThunk(
  "planTasks/claimSessionTaskApi",
  async ({ sessionId, taskId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const userId = getState().user.user?.id;
      const response = await axiosInstance(
        `/plan-session/${sessionId}/task/${taskId}`,
        "patch",
        { assigned_to_user_id: userId },
      );
      return response.data?.data || response.data;
    } catch (err) {
      console.error("[planTasks] Failed to claim task:", err);
      const session = getState().planTasks.session;
      if (session?.squad_id && session?.capsule_id) {
        dispatch(
          fetchPlanSession({
            squadId: session.squad_id,
            capsuleId: session.capsule_id,
          }),
        );
      }
      return rejectWithValue(
        err?.response?.data?.message || "Failed to claim task",
      );
    }
  },
);

/**
 * Unclaim a task within a planning session (Phase 2).
 * PATCH /api/v1/plan-session/{sessionId}/task/{taskId}
 * Only the assignee can unclaim their own task.
 */
export const unclaimSessionTaskApi = createAsyncThunk(
  "planTasks/unclaimSessionTaskApi",
  async ({ sessionId, taskId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const response = await axiosInstance(
        `/plan-session/${sessionId}/task/${taskId}`,
        "patch",
        { assigned_to_user_id: null },
      );
      return response.data?.data || response.data;
    } catch (err) {
      console.error("[planTasks] Failed to unclaim task:", err);
      const session = getState().planTasks.session;
      if (session?.squad_id && session?.capsule_id) {
        dispatch(
          fetchPlanSession({
            squadId: session.squad_id,
            capsuleId: session.capsule_id,
          }),
        );
      }
      return rejectWithValue(
        err?.response?.data?.message || "Failed to unclaim task",
      );
    }
  },
);

/**
 * Promote a session task to a real quest (finalize with why/done/approach).
 * POST /api/v1/plan-session/{sessionId}/finalize/{taskId}
 */
export const promoteSessionTask = createAsyncThunk(
  "planTasks/promoteSessionTask",
  async (
    {
      sessionId,
      taskId,
      why,
      done,
      approach,
      milestoneId,
      creationDurationSeconds,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstance(
        `/plan-session/${sessionId}/finalize/${taskId}`,
        "post",
        {
          task_why: why,
          task_done: done,
          task_approach: approach,
          milestone_id: milestoneId,
          // P7: define-duration telemetry. Serialize ONLY when non-null so
          // old-bundle / no-timer callers send a byte-identical legacy body
          // (undefined values are dropped by JSON.stringify). Backend
          // sanitizes and NULLs anything malformed - it never 422-blocks.
          creation_duration_seconds:
            creationDurationSeconds != null
              ? creationDurationSeconds
              : undefined,
        },
      );
      return { ...(response.data?.data || response.data), taskId };
    } catch (err) {
      console.error("[planTasks] Failed to promote task:", err);
      // Reject with a structured object so the define chat can roll back to
      // the specific question that failed validation (e.g. an over-2000-char
      // answer). `message` is preserved for backward compatibility with any
      // consumer that reads err.payload.message; `fieldErrors` is the Laravel
      // validation `data` map ({ task_done: ["..."] }) when present.
      return rejectWithValue({
        message: err?.response?.data?.message || "Failed to finalize task",
        fieldErrors: err?.response?.data?.data || null,
      });
    }
  },
);

/**
 * Advance the session phase.
 * POST /api/v1/plan-session/{sessionId}/phase
 * Any squad member can advance (no consensus for v1).
 */
export const advanceSessionPhase = createAsyncThunk(
  "planTasks/advanceSessionPhase",
  async ({ sessionId, phase }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance(
        `/plan-session/${sessionId}/phase`,
        "post",
        { phase },
      );
      return response.data?.data || response.data;
    } catch (err) {
      console.error("[planTasks] Failed to advance phase:", err);
      return rejectWithValue(
        err?.response?.data?.message || "Failed to advance phase",
      );
    }
  },
);

const initialState = {
  // Tasks saved from Step 4 (LevelObjective) - not yet assigned to deliverables
  savedTasks: [],
  // Tasks being planned - assigned to deliverables and/or claimed
  planningTasks: [],
  // Deliverables parsed from milestone data
  deliverables: [],

  // Mission Board session state (from backend)
  session: null,
  sessionTasks: [],
  sessionPhase: null,
  sessionPresence: { online: [], ready: [] },
  sessionLoading: false,
  sessionError: null,

  // Bug 3: track in-flight task creation at Redux level.
  // isTaskCreationPending is derived from taskCreationInFlight (a count,
  // not a boolean) so two concurrent producers - the TaskBoard inline
  // input and the G.2c SuggestedTasksRow - compose correctly. See the
  // createSessionTask reducers.
  isTaskCreationPending: false,
  taskCreationInFlight: 0,

  // M2: the newest session request and the squad it asked for. A context
  // change clears the session and voids every request in flight
  // (CONTEXT_CHANGED), so a session for squad A can never land over squad B.
  sessionRequestId: null,
  sessionRequestSquadId: null,
};

const sessionSquadIdOf = (value) => {
  const n = Number(value);
  return value !== null &&
    value !== undefined &&
    Number.isSafeInteger(n) &&
    n > 0
    ? n
    : null;
};

/* A reply is current when no request is recorded (a hand-built store) or it
   is the newest one. CONTEXT_CHANGED matches no reply. */
const isLatestSession = (state, action) =>
  state.sessionRequestId === null ||
  action.meta?.requestId === state.sessionRequestId;

const planTasksSlice = createSlice({
  name: "planTasks",
  initialState,
  reducers: {
    // Set saved tasks from Step 4 (LevelObjective)
    setSavedTasks: (state, action) => {
      state.savedTasks = action.payload.map((title, index) => ({
        id: generateId(),
        title,
        deliverableIndex: null,
        assignedTo: null,
        createdFromSuggestion: true,
      }));
      // Clear planning tasks when new saved tasks come in
      state.planningTasks = [];
    },

    // Set deliverables from milestone data
    setDeliverables: (state, action) => {
      state.deliverables = action.payload;
    },

    // Add a new task (created inline in Phase 1)
    addTask: (state, action) => {
      const { title, deliverableIndex } = action.payload;
      const newTask = {
        id: generateId(),
        title,
        deliverableIndex,
        assignedTo: null,
        createdFromSuggestion: false,
      };
      state.planningTasks.push(newTask);
    },

    // Move task from saved to planning (assign to deliverable)
    assignTaskToDeliverable: (state, action) => {
      const { taskId, deliverableIndex } = action.payload;

      // Check if task is in savedTasks
      const savedIndex = state.savedTasks.findIndex((t) => t.id === taskId);
      if (savedIndex !== -1) {
        const task = { ...state.savedTasks[savedIndex], deliverableIndex };
        state.savedTasks.splice(savedIndex, 1);
        state.planningTasks.push(task);
        return;
      }

      // Check if task is in planningTasks (moving between deliverables)
      const planningTask = state.planningTasks.find((t) => t.id === taskId);
      if (planningTask) {
        planningTask.deliverableIndex = deliverableIndex;
      }
    },

    // Remove task from deliverable (move back to saved)
    removeTaskFromDeliverable: (state, action) => {
      const { taskId } = action.payload;
      const taskIndex = state.planningTasks.findIndex((t) => t.id === taskId);

      if (taskIndex !== -1) {
        const task = state.planningTasks[taskIndex];
        // Only move back to saved if it was from a suggestion
        if (task.createdFromSuggestion) {
          state.planningTasks.splice(taskIndex, 1);
          state.savedTasks.push({
            ...task,
            deliverableIndex: null,
            assignedTo: null,
          });
        } else {
          // For manually created tasks, just remove them
          state.planningTasks.splice(taskIndex, 1);
        }
      }
    },

    // Claim a task (Phase 2)
    claimTask: (state, action) => {
      const { taskId, userId } = action.payload;
      const task = state.planningTasks.find((t) => t.id === taskId);
      if (task) {
        task.assignedTo = userId;
      }
    },

    // Unclaim a task (Phase 2)
    unclaimTask: (state, action) => {
      const { taskId } = action.payload;
      const task = state.planningTasks.find((t) => t.id === taskId);
      if (task) {
        task.assignedTo = null;
      }
    },

    // Delete a task entirely
    deleteTask: (state, action) => {
      const { taskId } = action.payload;
      state.savedTasks = state.savedTasks.filter((t) => t.id !== taskId);
      state.planningTasks = state.planningTasks.filter((t) => t.id !== taskId);
    },

    // Add a session task from WebSocket broadcast (B3)
    addSessionTask: (state, action) => {
      if (
        !state.sessionTasks.find((t) =>
          sameSessionTaskId(t.id, action.payload.id),
        )
      ) {
        state.sessionTasks.push(action.payload);
      }
    },

    // Remove a session task from WebSocket broadcast
    removeSessionTask: (state, action) => {
      state.sessionTasks = state.sessionTasks.filter(
        (t) => !sameSessionTaskId(t.id, action.payload),
      );
    },

    // Edit a session task title
    editSessionTask: (state, action) => {
      const { taskId, title } = action.payload;
      const task = state.sessionTasks.find((t) =>
        sameSessionTaskId(t.id, taskId),
      );
      if (task) {
        task.title = title;
      }
    },

    // Claim a session task from WebSocket broadcast
    claimSessionTask: (state, action) => {
      const { taskId, userId } = action.payload;
      const task = state.sessionTasks.find((t) =>
        sameSessionTaskId(t.id, taskId),
      );
      if (task) {
        task.assigned_to_user_id = userId;
      }
    },

    // Unclaim a session task from WebSocket broadcast
    unclaimSessionTask: (state, action) => {
      const { taskId } = action.payload;
      const task = state.sessionTasks.find((t) =>
        sameSessionTaskId(t.id, taskId),
      );
      if (task) {
        task.assigned_to_user_id = null;
      }
    },

    // Mark a session task as finalized (promoted to quest)
    finalizeSessionTask: (state, action) => {
      const { taskId, questId } = action.payload;
      const task = state.sessionTasks.find((t) =>
        sameSessionTaskId(t.id, taskId),
      );
      if (task) {
        task.quest_id = questId;
      }
    },

    // Update session phase from WebSocket broadcast
    setSessionPhase: (state, action) => {
      state.sessionPhase = action.payload;
    },

    // Update session presence from WebSocket broadcast
    setSessionPresence: (state, action) => {
      state.sessionPresence = action.payload;
    },

    // Reset all plan tasks (when closing drawer or completing)
    resetPlanTasks: (state) => {
      state.savedTasks = [];
      state.planningTasks = [];
      state.deliverables = [];
      state.session = null;
      state.sessionTasks = [];
      state.sessionPhase = null;
      state.sessionPresence = { online: [], ready: [] };
      state.sessionLoading = false;
      state.sessionError = null;
      state.isTaskCreationPending = false;
      state.taskCreationInFlight = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlanSession.pending, (state, action) => {
        state.sessionLoading = true;
        state.sessionError = null;
        state.sessionRequestId = action.meta?.requestId ?? null;
        state.sessionRequestSquadId = sessionSquadIdOf(
          action.meta?.arg?.squadId,
        );
      })
      .addCase(fetchPlanSession.fulfilled, (state, action) => {
        if (!isLatestSession(state, action)) return;
        state.sessionLoading = false;
        const data = action.payload;
        state.session = data?.session || data;
        state.sessionTasks = data?.tasks || data?.session?.tasks || [];
        state.sessionPhase =
          data?.session?.phase || data?.phase || "plan_together";
        state.sessionPresence = data?.presence || { online: [], ready: [] };
      })
      .addCase(fetchPlanSession.rejected, (state, action) => {
        if (!isLatestSession(state, action)) return;
        state.sessionLoading = false;
        state.sessionError = action.payload || "Failed to load session";
      })
      .addCase(createSessionTask.pending, (state) => {
        // Reference count, not a boolean: createSessionTask now has TWO
        // concurrent producers (the TaskBoard inline input AND the G.2c
        // SuggestedTasksRow adopt / "add all"). A single boolean cleared
        // on the FIRST .fulfilled while a second create is still in
        // flight, so the "move to claiming" advance gate (which reads
        // !isTaskCreationPending in MissionBoard) un-blocked early and a
        // student could advance with a task still saving. The count is
        // true iff >=1 create is genuinely in flight and clears only
        // when ALL of them settle. Clamped >=0 so a stray settle (e.g.
        // a settle that arrives after resetPlanTasks zeroed the count on
        // drawer close) cannot drive it negative and wedge the gate.
        state.taskCreationInFlight = (state.taskCreationInFlight || 0) + 1;
        state.isTaskCreationPending = state.taskCreationInFlight > 0;
      })
      .addCase(createSessionTask.fulfilled, (state, action) => {
        state.taskCreationInFlight = Math.max(
          0,
          (state.taskCreationInFlight || 0) - 1,
        );
        state.isTaskCreationPending = state.taskCreationInFlight > 0;
        if (!state.session) return;
        if (
          !state.sessionTasks.find((t) =>
            sameSessionTaskId(t.id, action.payload.id),
          )
        ) {
          state.sessionTasks.push(action.payload);
        }
      })
      .addCase(createSessionTask.rejected, (state) => {
        state.taskCreationInFlight = Math.max(
          0,
          (state.taskCreationInFlight || 0) - 1,
        );
        state.isTaskCreationPending = state.taskCreationInFlight > 0;
      })
      .addCase(updateSessionTaskTitle.fulfilled, (state, action) => {
        if (!state.session) return;
        const updated = action.payload;
        const task = state.sessionTasks.find((t) =>
          sameSessionTaskId(t.id, updated.id),
        );
        if (task) {
          task.title = updated.title;
        }
      })
      .addCase(deleteSessionTask.fulfilled, (state, action) => {
        if (!state.session) return;
        state.sessionTasks = state.sessionTasks.filter(
          (t) => !sameSessionTaskId(t.id, action.payload),
        );
      })
      .addCase(claimSessionTaskApi.fulfilled, (state, action) => {
        if (!state.session) return;
        const updated = action.payload;
        const task = state.sessionTasks.find((t) =>
          sameSessionTaskId(t.id, updated.id),
        );
        if (task) {
          task.assigned_to_user_id = updated.assigned_to_user_id;
        }
      })
      .addCase(unclaimSessionTaskApi.fulfilled, (state, action) => {
        if (!state.session) return;
        const updated = action.payload;
        const task = state.sessionTasks.find((t) =>
          sameSessionTaskId(t.id, updated.id),
        );
        if (task) {
          task.assigned_to_user_id = updated.assigned_to_user_id ?? null;
        }
      })
      .addCase(promoteSessionTask.fulfilled, (state, action) => {
        if (!state.session) return;
        const taskId = action.meta.arg.taskId;
        const task = state.sessionTasks.find((t) =>
          sameSessionTaskId(t.id, taskId),
        );
        if (task) {
          task.quest_id = action.payload.id || action.payload.quest_id;
        }
      })
      .addCase(advanceSessionPhase.fulfilled, (state, action) => {
        if (!state.session) return;
        const data = action.payload;
        state.sessionPhase =
          data?.session?.phase || data?.phase || state.sessionPhase;
      })
      // M2: the active context moved to another squad (or to none). The held
      // session belongs to the previous one: clear it and void its requests,
      // so no presence, task or claim call can target the old session.
      .addMatcher(
        (action) => settledActiveSquadOf(action) !== undefined,
        (state, action) => {
          const next = settledActiveSquadOf(action);
          const held =
            sessionSquadIdOf(state.session?.squad_id) ??
            state.sessionRequestSquadId;
          if (held === null || held === next) return;
          state.session = null;
          state.sessionTasks = [];
          state.sessionPhase = null;
          state.sessionPresence = { online: [], ready: [] };
          state.sessionLoading = false;
          state.sessionError = null;
          state.isTaskCreationPending = false;
          state.taskCreationInFlight = 0;
          state.sessionRequestId = CONTEXT_CHANGED;
          state.sessionRequestSquadId = null;
        },
      );
  },
});

export const {
  setSavedTasks,
  setDeliverables,
  addTask,
  assignTaskToDeliverable,
  removeTaskFromDeliverable,
  claimTask,
  unclaimTask,
  deleteTask,
  addSessionTask,
  removeSessionTask,
  editSessionTask,
  claimSessionTask,
  unclaimSessionTask,
  finalizeSessionTask,
  setSessionPhase,
  setSessionPresence,
  resetPlanTasks,
} = planTasksSlice.actions;

export default planTasksSlice.reducer;
